import {
  PANEL_DESCRIPTIONS,
  PANEL_IDS,
  PANEL_TITLES,
  type PanelId,
} from '@trajectory/ui';
import { resetLayout, saveCurrentLayoutAsDefault, togglePanel } from '@/stores/layoutStore';
import { useSettingsStore } from '@/stores/settingsStore';
import { useUiStore } from '@/stores/uiStore';

export interface Command {
  id: string;
  title: string;
  description: string;
  category: string;
  shortcut?: string;
  run: () => void;
}

function panelCommand(id: PanelId): Command {
  return {
    id: `panel.toggle.${id}`,
    title: `Toggle ${PANEL_TITLES[id]} panel`,
    description: PANEL_DESCRIPTIONS[id],
    category: 'View',
    run: () => togglePanel(id),
  };
}

/**
 * The single source of truth for anything the app can *do* from outside its
 * originating panel: the command palette, the toolbar and keyboard shortcuts
 * all dispatch through this list rather than each wiring up their own copy.
 */
export function getCommands(): Command[] {
  return [
    {
      id: 'theme.toggle',
      title: 'Toggle dark / light theme',
      description: 'Switch the interface colour theme without changing mission data.',
      category: 'Preferences',
      shortcut: 'Ctrl+Alt+T',
      run: () => {
        const { themeMode, setThemeMode } = useSettingsStore.getState();
        const systemIsLight = window.matchMedia('(prefers-color-scheme: light)').matches;
        const resolved = themeMode === 'system' ? (systemIsLight ? 'light' : 'dark') : themeMode;
        setThemeMode(resolved === 'dark' ? 'light' : 'dark');
      },
    },
    {
      id: 'autosave.toggle',
      title: 'Toggle autosave',
      description: 'Enable or pause debounced version-safe saving of mission and rocket edits.',
      category: 'Preferences',
      run: () => {
        const { autosaveEnabled, setAutosaveEnabled } = useSettingsStore.getState();
        setAutosaveEnabled(!autosaveEnabled);
        useUiStore.getState().notify({
          kind: 'info',
          title: autosaveEnabled ? 'Autosave off' : 'Autosave on',
          message: autosaveEnabled
            ? 'Edits are kept locally until autosave is switched back on.'
            : 'Pending edits will be saved shortly.',
        });
      },
    },
    {
      id: 'motion.toggle',
      title: 'Toggle reduced motion',
      description: 'Reduce camera, star-field, pulse, and playback animation where supported.',
      category: 'Preferences',
      run: () => {
        useSettingsStore.getState().toggleReducedMotion();
        useUiStore.getState().notify({
          kind: 'info',
          title: useSettingsStore.getState().reducedMotion ? 'Reduced motion on' : 'Reduced motion off',
        });
      },
    },
    {
      id: 'education.open',
      title: 'Open learning and reference centre',
      description: 'Browse aerospace definitions, student workflows, operation effects, and model limitations.',
      category: 'Help',
      shortcut: '?',
      run: () => useUiStore.getState().openEducationCenter(),
    },
    {
      id: 'experience.toggle',
      title: 'Toggle guided / professional workspace',
      description: 'Show or hide contextual learning banners without changing simulation fidelity.',
      category: 'Help',
      run: () => {
        const { experienceLevel, setExperienceLevel } = useSettingsStore.getState();
        const next = experienceLevel === 'guided' ? 'professional' : 'guided';
        setExperienceLevel(next);
        useUiStore.getState().notify({
          kind: 'info',
          title: next === 'guided' ? 'Guided learning enabled' : 'Professional workspace enabled',
          message:
            next === 'guided'
              ? 'Contextual explanations are visible; physics and tools are unchanged.'
              : 'Context banners are hidden; the glossary remains available from Learn.',
        });
      },
    },
    {
      id: 'layout.reset',
      title: 'Reset panel layout to default',
      description: 'Restore the factory panel arrangement; mission and rocket data are not changed.',
      category: 'View',
      run: () => resetLayout(),
    },
    {
      id: 'layout.saveDefault',
      title: 'Save current panel layout as default',
      description: 'Use the current dock arrangement the next time the application opens.',
      category: 'View',
      run: () => {
        saveCurrentLayoutAsDefault();
        useUiStore.getState().notify({ kind: 'success', title: 'Layout saved as default' });
      },
    },
    ...PANEL_IDS.map(panelCommand),
  ];
}
